import {
  blockFromClick,
  effectiveQuickDurationMin,
  wallMinOf,
  MINUTES_PER_DAY,
  type CreateOptions,
  type LiveTimeBlock,
  type Span,
  type LiveProject,
  isProjectSelectable,
} from '@wiztime/core';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { dayPath } from '../../../app/routes';
import { cx } from '../../../lib/cx';
import { LayoutMode, useLayoutMode } from '../../../hooks/useMediaQuery';
import { formatRange } from '../../../lib/format';
import { projectHex } from '../../../lib/projectColor';
import { TooltipBubble, useTooltip } from '../../../ui/Tooltip';
import { toastInfo } from '../../../shell/toastStore';
import { useDayData } from '../DayDataContext';
import { ContextMenuSource, PickerKind, useDayUi, type AnchorRect } from '../dayUiStore';
import {
  createHintActive,
  moveHintActive,
  resizeHintActive,
  useGestureCounts,
  type GestureKind,
} from '../gestureCount';
import { QUICK_MARK_OFF, QuickMarkMode, type QuickMarkState } from '../quickMark';
import { addBesideGroup } from '../tileMark';
import { useDayCommands } from '../useDayCommands';
import { DragGhost, GhostVariant } from './DragGhost';
import { FloatTip, type TipLine } from './FloatTip';
import { beginQuickMarkWrite, endQuickMarkWrite } from './quickMarkGuard';
import { registerLaneDropTarget, type LaneDropTarget } from './laneDropTargets';
import {
  createBandPx,
  defaultLaneItem,
  gapItemId,
  gapSlots,
  LaneItemKind,
  laneItems,
  LaneStep,
  LaneVariant,
  MIN_OVERLAP_TEXT_COLUMN_PX,
  MIN_TEXT_BLOCK_PX,
  percentOf,
  sleepBands,
  stepLaneItem,
  stolenSpans,
  type DisplayGroup,
} from './laneModel';
import { NowLine } from './NowLine';
import { STACK_OFFSET_DAY_PX, STACK_OFFSET_WEEK_PX, TimeBlockCard } from './TimeBlockCard';
import { useElementWidth } from './useElementWidth';
import styles from './Timeline.module.css';
import { dragTipLine2 } from './tipLine';
import {
  ArmedKind,
  PreviewKind,
  useLaneInteractions,
  type CrossDayGrid,
  type LaneCommands,
} from './useLaneInteractions';

/** The hover ghost carries its two text lines only from this height on (ui-redesign §3.4.1). */
const GHOST_TEXT_MIN_PX = 40;
/** One text line fits: the hover ghost then still shows its start-end range (2026-10c §1). */
const GHOST_RANGE_MIN_PX = 16;

interface BlockLaneProps {
  readonly laneRef: RefObject<HTMLDivElement | null>;
  readonly laneHeightPx: number;
  /** `Week`: one narrow column of the week view (shorter block text, link to the day view). */
  readonly variant?: LaneVariant;
  /** Week view only: the grid of seven day columns a whole-block move may cross (§2.8). */
  readonly crossDay?: CrossDayGrid;
}

const SKELETON_BARS = [
  { top: 4, height: 18 },
  { top: 30, height: 12 },
  { top: 52, height: 24 },
  { top: 82, height: 10 },
];

/** Keys that move focus along the lane's items (requirements §5.2). */
const LANE_KEY_STEPS: Readonly<Record<string, LaneStep | undefined>> = {
  ArrowUp: LaneStep.Previous,
  ArrowDown: LaneStep.Next,
  Home: LaneStep.First,
  End: LaneStep.Last,
};

/** First non-empty line of the first non-empty note among the group's members (ui-redesign §3.7). */
function noteFirstLineOf(group: DisplayGroup): string | undefined {
  for (const member of group.members) {
    const note = member.data.note;
    if (note === null) continue;
    for (const line of note.split('\n')) {
      const text = line.trim();
      if (text !== '') return text;
    }
  }
  return undefined;
}

function withSpan(block: LiveTimeBlock, startMin: number, endMin: number): LiveTimeBlock {
  return { ...block, data: { ...block.data, startMin, endMin } };
}

/** A project quick mark can still mark with: active, and for a child its parent active too. */
function isSelectableProjectId(byId: ReadonlyMap<string, LiveProject>, id: string): boolean {
  const project = byId.get(id);
  return project !== undefined && isProjectSelectable(project, byId);
}

/**
 * The block area of the date in `DayDataContext`: sleep shading, "+ Log" slots, blocks, previews
 * and the now line. 328px wide in the day view; one equal-width column in the week view.
 */
export function BlockLane({ laneRef, laneHeightPx, variant = LaneVariant.Day, crossDay }: BlockLaneProps) {
  const { t } = useTranslation();
  const data = useDayData();
  const { date, settings, projects, blocks, groups, conflicts, now } = data;
  const commands = useDayCommands();
  const selectedKey = useDayUi((state) => state.selectedKey);
  const picker = useDayUi((state) => state.picker);
  const conflictOpen = useDayUi((state) => state.conflict !== null);
  const quickMarkId = useDayUi((state) => state.quickMark.projectId);
  const quickMarkLockedOn = useDayUi((state) => state.quickMark.mode === QuickMarkMode.Locked);
  const resolving = useDayUi((state) => state.conflict);
  const focusRequest = useDayUi((state) => state.focusRequest);
  const { select, openPicker } = useDayUi.getState();
  const narrow = useLayoutMode() === LayoutMode.Narrow;
  const tooltip = useTooltip();
  const tooltipId = `tip-${useId()}`;
  const [activeId, setActiveId] = useState<string | null>(null);
  // Lane width drives the overlap column pixels: columns under 56px show no text (§3.1.4).
  const laneWidthPx = useElementWidth(laneRef);

  const quickProjectId =
    quickMarkId !== null && isSelectableProjectId(projects.byId, quickMarkId) ? quickMarkId : null;
  const quickProjectForLength = quickProjectId !== null ? projects.byId.get(quickProjectId) : undefined;
  const options: CreateOptions = {
    step: settings.timeStepMin,
    minDuration: settings.minDurationMin,
    dayStartMin: settings.dayStartMin,
    // A quick mark creates a block of the project's own length (2026-10c §2); everything else
    // keeps the default block duration.
    defaultDurationMin:
      quickProjectForLength === undefined
        ? settings.defaultDurationMin
        : effectiveQuickDurationMin(quickProjectForLength.data, settings),
  };
  const ready = !data.blocksLoading && data.blocksError === null;
  // A quick-mark tile dragged from outside the lane reads the lane through this registration
  // (2026-10c §2); the ref keeps the registered reader current without re-registering.
  const overlayOpen = picker !== null || conflictOpen;
  const dropTarget = useRef<LaneDropTarget | null>(null);
  useEffect(() => {
    dropTarget.current = {
      date,
      options,
      obstacles: blocks.map((block) => block.data),
      ready: ready && !overlayOpen,
    };
  });
  useEffect(() => {
    const lane = laneRef.current;
    if (lane === null) return;
    return registerLaneDropTarget(lane, () => {
      const target = dropTarget.current;
      if (target === null) return null;
      // No drop while the context menu or the help overlay is open (the picker and the overlap
      // popover are folded into `ready` above); an overlay is also found above the lane by the hit test.
      const { helpOpen, contextMenu } = useDayUi.getState();
      return helpOpen || contextMenu !== null ? { ...target, ready: false } : target;
    });
  }, [laneRef]);
  const tileDrag = useDayUi((state) => (state.tileDrag?.date === date ? state.tileDrag : null));

  // Learning counters (ui-redesign §3.6.2): committed gestures retire the beginner hints. The
  // counts live in a shared store, so a gesture committed in one of the seven week lanes (or in
  // the day lane) retires the hints in all of them at once; mounting re-reads the device storage.
  const gestureCounts = useGestureCounts((state) => state.counts);
  useEffect(() => {
    useGestureCounts.getState().refresh();
  }, []);
  const onGesture = useCallback((kind: GestureKind) => useGestureCounts.getState().record(kind), []);

  const quickProject = quickProjectId !== null ? projects.byId.get(quickProjectId) : undefined;
  // Quick mark never replaces a block (design 2026-10-15 §1.2): a click on an existing block or
  // merged group adds a parallel block with the same span instead.
  const onQuickAdd = useCallback(
    (group: DisplayGroup): void => {
      const state = useDayUi.getState();
      // Captured at command start (§4.3): a quick-mark state entered while the write is in
      // flight keeps its own "once" — the late outcome settles nothing then.
      const startedFrom = state.quickMark;
      const quickId = startedFrom.projectId;
      if (quickId === null || projects.byId.get(quickId) === undefined) return;
      // A second click while the first write is in flight is ignored (shared guard).
      if (!beginQuickMarkWrite()) return;
      const span = { startMin: group.startMin, endMin: group.endMin };
      // The "once" is consumed only when a block was actually added (§4.3): the same project
      // writes nothing and keeps it.
      void addBesideGroup({ date, span, projectId: group.projectId }, quickId, commands.createBlock, t, {
        select: false,
      })
        .then((outcome) => {
          useDayUi.getState().quickMarkSettle(outcome, startedFrom);
        })
        .finally(endQuickMarkWrite);
    },
    [projects, commands, t, date],
  );

  // The lane hook starts quick-mark creates itself, so the state snapshot the settle compares
  // against (§4.3) is captured where the command starts: in this wrapper around `createBlock`.
  // Quick-mark creates are serialized by the hook's in-flight guard, so one slot suffices.
  const quickStartedFrom = useRef<QuickMarkState>(QUICK_MARK_OFF);
  const laneCommands = useMemo<LaneCommands>(
    () => ({
      ...commands,
      createBlock: (createDate, span, projectId) => {
        quickStartedFrom.current = useDayUi.getState().quickMark;
        return commands.createBlock(createDate, span, projectId);
      },
    }),
    [commands],
  );

  const interactions = useLaneInteractions({
    date,
    laneRef,
    options,
    blocks,
    groups,
    quickProjectId,
    selectedKey,
    overlayOpen,
    enabled: ready,
    hoverEnabled: ready && !overlayOpen,
    crossDay,
    commands: laneCommands,
    onSelect: select,
    onCreateRequest: (span, anchor) => openPicker({ kind: PickerKind.Create, date, span, anchor }),
    onGroupClick: (group, anchor) => openPicker({ kind: PickerKind.Edit, date, groupKey: group.key, anchor }),
    onOverlayDismiss: () => useDayUi.getState().closeOverlays(),
    onBlockContextMenu: (group, point) => {
      const ui = useDayUi.getState();
      ui.openContextMenu({
        date,
        groupKey: group.key,
        x: point.x + 2,
        y: point.y + 2,
        source: ContextMenuSource.Pointer,
      });
    },
    onQuickAdd,
    onQuickMark: (outcome) => useDayUi.getState().quickMarkSettle(outcome, quickStartedFrom.current),
    onRemoteCancel: () => toastInfo(t('toast.remoteEdited')),
    onGesture,
  });
  const { preview, hoverSpan, dragPointRef, armed, armedGroupKey } = interactions;

  // While a drag is in progress the other blocks' layout is frozen (ui-redesign §3.2): a whole
  // move keeps every group untouched (the dragged cards translate by `--drag-shift`), and a
  // resize rebuilds only the dragged group — cut members drawn short, deleted members hidden,
  // no re-merging with neighbours (§2.3).
  const draggedKey = preview?.groupKey ?? null;
  const shownGroups = useMemo<readonly DisplayGroup[]>(() => {
    if (preview === null || draggedKey === null || preview.kind !== PreviewKind.Resize) return groups;
    const changed = new Map((preview.group?.changes ?? []).map((change) => [change.id, change]));
    const deleted = new Set(preview.group?.deleteIds ?? []);
    return groups.flatMap((group) => {
      if (group.key !== draggedKey) return [group];
      const members = group.members
        .filter((member) => !deleted.has(member.id))
        .map((member) => {
          const change = changed.get(member.id);
          if (change !== undefined) return withSpan(member, change.startMin, change.endMin);
          if (preview.group === null && member.id === preview.blockId) {
            return withSpan(member, preview.span.startMin, preview.span.endMin);
          }
          return member;
        });
      if (members.length === 0) return [];
      return [
        {
          ...group,
          members,
          memberIds: members.map((member) => member.id),
          startMin: preview.span.startMin,
          endMin: preview.span.endMin,
        },
      ];
    });
  }, [groups, preview, draggedKey]);
  // The gaps are frozen too: they follow the committed blocks, not the preview.
  const slots = useMemo(
    () =>
      ready
        ? gapSlots(
            blocks.map((block) => block.data),
            settings.dayStartMin,
            settings.defaultDurationMin,
            settings.timeStepMin,
            settings.minDurationMin,
          )
        : [],
    [
      ready,
      blocks,
      settings.dayStartMin,
      settings.defaultDurationMin,
      settings.timeStepMin,
      settings.minDurationMin,
    ],
  );
  const bands = sleepBands(settings.sleepStartMin, settings.sleepEndMin, settings.dayStartMin);
  const nowWallMin = wallMinOf(now, date);
  const nowMin =
    nowWallMin >= settings.dayStartMin && nowWallMin < settings.dayStartMin + MINUTES_PER_DAY
      ? nowWallMin
      : null;

  const items = useMemo(() => laneItems(shownGroups, slots), [shownGroups, slots]);
  const groupByKey = useMemo(() => new Map(shownGroups.map((group) => [group.key, group])), [shownGroups]);
  const slotByStart = useMemo(() => new Map(slots.map((slot) => [slot.startMin, slot])), [slots]);
  // The lane is one tab stop (roving tabindex): the last item focused, else the next one still to come.
  const tabStopId =
    activeId !== null && items.some((item) => item.id === activeId)
      ? activeId
      : defaultLaneItem(items, nowMin);

  const itemElement = useCallback(
    (id: string): HTMLElement | undefined =>
      Array.from(laneRef.current?.querySelectorAll<HTMLElement>('[data-lane-item]') ?? []).find(
        (element) => element.dataset['laneItem'] === id,
      ),
    [laneRef],
  );

  // A block just created, re-projected or confirmed takes keyboard focus as soon as it is
  // drawn, and becomes the selected block (ui-redesign §3.2.1: a finished creation selects the
  // new block, so the arrow keys and Del act on it right away). Selecting before focusing keeps
  // onLaneFocus from reading the focus change as a move away from the selection.
  useEffect(() => {
    if (focusRequest === null) return;
    const group = groups.find((candidate) => candidate.memberIds.includes(focusRequest.blockId));
    if (group === undefined) return;
    const element = Array.from(laneRef.current?.querySelectorAll<HTMLElement>('[data-group-key]') ?? []).find(
      (candidate) => candidate.dataset['groupKey'] === group.key,
    );
    if (element === undefined) return;
    useDayUi.getState().selectAuto(group.key);
    element.focus();
    useDayUi.getState().clearFocusRequest(focusRequest.id);
  }, [focusRequest, groups, laneRef]);

  const onLaneFocus = (event: FocusEvent<HTMLElement>): void => {
    tooltip.handlers.onFocus(event);
    const element = event.target;
    if (!(element instanceof HTMLElement) || element.dataset['laneItem'] === undefined) return;
    setActiveId(element.dataset['laneItem']);
    // The arrow keys nudge the selected block; moving focus to anything else ends that.
    const selected = useDayUi.getState().selectedKey;
    if (selected !== null && selected !== (element.dataset['groupKey'] ?? null)) select(null);
  };

  const onLaneKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    const element = event.target;
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    if (!(element instanceof HTMLElement) || element.dataset['laneItem'] === undefined) return;
    const step = LANE_KEY_STEPS[event.key];
    if (step === undefined) return;
    const vertical = step === LaneStep.Previous || step === LaneStep.Next;
    // Arrows with Shift, or on the selected block, adjust its edges (useDayKeyboard) instead of moving.
    if (vertical) {
      const selected = useDayUi.getState().selectedKey;
      if (event.shiftKey || (selected !== null && selected === element.dataset['groupKey'])) return;
    }
    event.preventDefault();
    const target = stepLaneItem(items, element.dataset['laneItem'], step);
    if (target !== null && target !== element.dataset['laneItem']) itemElement(target)?.focus();
  };

  // Stable callbacks keep the memoised block cards from rendering on every change of the lane.
  // Opening with the keyboard (Enter/Space on the block) also selects it (ui-redesign §3.2.1),
  // so Alt moves, Del and Ctrl/Cmd+Enter keep a target while the picker holds the focus (§3.5).
  const openGroup = useCallback(
    (opened: DisplayGroup, anchor: AnchorRect): void => {
      const ui = useDayUi.getState();
      ui.select(opened.key);
      ui.openPicker({ kind: PickerKind.Edit, date, groupKey: opened.key, anchor });
    },
    [date],
  );
  const conflictPairs = conflicts.pairs;
  const openGroupConflict = useCallback(
    (opened: DisplayGroup, anchor: AnchorRect): void => {
      if (!conflictPairs.some(([a, b]) => a === opened.key || b === opened.key)) return;
      // The popover sits beside the lane, so it never covers the blocks it is about (§3.8).
      const lane = laneRef.current?.getBoundingClientRect();
      const beside = lane === undefined ? anchor : { ...anchor, left: lane.left, right: lane.right };
      useDayUi.getState().openConflict({ date, id: opened.key, anchor: beside });
      // The bottom sheet of a phone covers the lower part of the screen: bring the block above it.
      if (narrow) {
        const element = Array.from(
          laneRef.current?.querySelectorAll<HTMLElement>('[data-group-key]') ?? [],
        ).find((candidate) => candidate.dataset['groupKey'] === opened.key);
        element?.scrollIntoView({ block: 'center' });
      }
    },
    [conflictPairs, date, laneRef, narrow],
  );

  // Blocks ringed while the overlap popover is open: the subject and every block it overlaps.
  const resolvingIds = useMemo<ReadonlySet<string>>(() => {
    if (resolving === null || resolving.date !== date) return new Set();
    const ids = new Set([resolving.id]);
    for (const [a, b] of conflictPairs) {
      if (a === resolving.id) ids.add(b);
      if (b === resolving.id) ids.add(a);
    }
    return ids;
  }, [resolving, date, conflictPairs]);

  // Stretches counted towards another block, for the hatch (§2.1.5); frozen data during a drag.
  const stolen = useMemo(() => stolenSpans(blocks, date), [blocks, date]);

  const createFromSlot = (startMin: number, anchor: AnchorRect): void => {
    const span = blockFromClick(
      startMin,
      options,
      blocks.map((block) => block.data),
    );
    if (span === null) return;
    if (quickProjectId !== null) {
      // Captured at command start (§4.3): a late outcome never settles a state entered since.
      const startedFrom = useDayUi.getState().quickMark;
      void commands.createBlock(date, span, quickProjectId).then((outcome) => {
        useDayUi.getState().quickMarkSettle(outcome, startedFrom);
      });
    } else openPicker({ kind: PickerKind.Create, date, span, anchor });
  };

  // The time axis mirrors the drag and the hover ghost as pills; the week view also draws the
  // guide lines of a drag across all seven lanes from this shared state.
  const previewSpan = preview?.span ?? null;
  useEffect(() => {
    useDayUi.getState().setDragSpan(previewSpan);
  }, [previewSpan]);
  const hoverStartMin = hoverSpan !== null && preview === null && !overlayOpen ? hoverSpan.startMin : null;
  // Seven week lanes share the store: a lane only clears the hover pill it set itself, so the
  // lane the pointer just entered is not wiped out by the one it left.
  const lastHoverMin = useRef<number | null>(null);
  useEffect(() => {
    const ui = useDayUi.getState();
    if (hoverStartMin !== null) ui.setHoverMin(hoverStartMin);
    else if (lastHoverMin.current !== null && ui.hoverMin === lastHoverMin.current) ui.setHoverMin(null);
    lastHoverMin.current = hoverStartMin;
  }, [hoverStartMin]);
  useEffect(
    () => () => {
      const ui = useDayUi.getState();
      ui.setDragSpan(null);
      ui.setHoverMin(null);
    },
    [],
  );

  // Whole-move preview: the dragged cards translate by this many pixels (ui-redesign §2.4).
  const movePreview = preview?.kind === PreviewKind.Move ? preview : null;
  const moveShiftPx = movePreview === null ? 0 : (movePreview.deltaMin * laneHeightPx) / MINUTES_PER_DAY;
  const draggedGroup = draggedKey === null ? undefined : groups.find((group) => group.key === draggedKey);
  // Cross-day move (week view, §2.8): the dragged cards also translate horizontally, onto the
  // target lane. All seven lanes share one grid row, so the left offset is the whole shift;
  // the layout effect measures it before paint, so the card never lags behind the switch.
  const crossTargetDate = movePreview?.targetDate ?? null;
  const [crossShiftPx, setCrossShiftPx] = useState(0);
  useLayoutEffect(() => {
    const source = laneRef.current;
    const target =
      crossTargetDate === null
        ? null
        : (crossDay?.gridRef.current?.querySelector<HTMLElement>(
            `[data-testid="lane"][data-date="${crossTargetDate}"]`,
          ) ?? null);
    setCrossShiftPx(
      source === null || target === null
        ? 0
        : target.getBoundingClientRect().left - source.getBoundingClientRect().left,
    );
  }, [crossTargetDate, crossDay, laneRef]);
  const originSpan: Span | null =
    movePreview !== null &&
    draggedGroup !== undefined &&
    (Math.abs(movePreview.deltaMin) >= settings.timeStepMin || crossTargetDate !== null)
      ? { startMin: draggedGroup.startMin, endMin: draggedGroup.endMin }
      : null;

  // Second line of the floating tip, by §3.4.3 priority: delete warning > draft confirmation >
  // overlap > flip > minimum duration (dragTipLine2 holds the rule, with its own tests). During
  // a cross-day move the overlap candidates are the target day's blocks (§2.8, hint §2.1.3).
  const tipLine2 = useMemo<TipLine | null>(
    () =>
      preview === null
        ? null
        : dragTipLine2({
            preview,
            blocks: crossTargetDate === null ? blocks : (crossDay?.blocksOf?.(crossTargetDate) ?? []),
            draggedGroup,
            minDurationMin: settings.minDurationMin,
            projectName: (projectId) =>
              projects.byId.get(projectId)?.data.name ?? t('timeline.unknownProject'),
            t,
          }),
    [preview, blocks, crossTargetDate, crossDay, draggedGroup, projects, settings.minDurationMin, t],
  );

  // Hover ghost content (§3.4.1): time range plus, during the learning period, the second
  // line. "Occupied" follows the creation criterion (2026-10b §3.3): the hover start lies
  // inside a block only when the pointer minute does — a release would overlay that block.
  const hoverOccupied =
    hoverSpan !== null &&
    blocks.some(
      (block) => block.data.startMin <= hoverSpan.startMin && hoverSpan.startMin < block.data.endMin,
    );
  const hoverGhostPx =
    hoverSpan === null ? 0 : ((hoverSpan.endMin - hoverSpan.startMin) * laneHeightPx) / MINUTES_PER_DAY;
  const ghostTextFits = variant === LaneVariant.Day && hoverGhostPx >= GHOST_TEXT_MIN_PX;
  // Without the half-hour lines the range is the only place to read the end of the hover ghost.
  const ghostRangeFits = variant === LaneVariant.Day && hoverGhostPx >= GHOST_RANGE_MIN_PX;
  const ghostHint = createHintActive(gestureCounts)
    ? t(hoverOccupied ? 'ghost.hintOverlay' : 'ghost.hintFree')
    : undefined;

  const quickColor = quickProject === undefined ? undefined : projectHex(quickProject, projects.byId);
  // The hover ghost's range carries the mode as a text suffix (2026-10b §4.4).
  const ghostSuffix =
    quickProject === undefined
      ? undefined
      : t(quickMarkLockedOn ? 'quickMark.ghostLocked' : 'quickMark.ghostOnce');

  // Block-tip learning line (§3.6.2): the gestures not yet learned, until each one retires.
  const learnLines = [
    ...(moveHintActive(gestureCounts) ? [t('tip.learnMove')] : []),
    ...(resizeHintActive(gestureCounts) ? [t('tip.learnResize')] : []),
  ];
  const learnHint = learnLines.length > 0 ? learnLines.join(' · ') : undefined;

  const pendingSpan = picker?.kind === PickerKind.Create && picker.date === date ? picker.span : null;
  // A gap shows its "+ Log" caption only when a text line fits and the pending block does not cover it.
  const showGapLabel = (slot: { readonly startMin: number; readonly endMin: number }): boolean =>
    ((slot.endMin - slot.startMin) * laneHeightPx) / MINUTES_PER_DAY >= MIN_TEXT_BLOCK_PX &&
    (pendingSpan === null || slot.endMin <= pendingSpan.startMin || slot.startMin >= pendingSpan.endMin);
  const showHover = hoverSpan !== null && preview === null && !overlayOpen;

  return (
    <div
      ref={laneRef}
      className={cx(
        styles.lane,
        quickProjectId !== null && styles.quickMark,
        variant === LaneVariant.Week && styles.weekLane,
      )}
      role="group"
      aria-label={variant === LaneVariant.Week ? t('week.laneLabel', { date }) : t('timeline.title')}
      aria-busy={data.blocksLoading}
      data-testid="lane"
      data-date={date}
      data-dragging={preview !== null ? '' : undefined}
      data-cross-target={crossTargetDate ?? undefined}
      data-cross-denied={preview?.crossDenied === true ? '' : undefined}
      {...interactions.handlers}
      onPointerDown={(event) => {
        tooltip.hide();
        interactions.handlers.onPointerDown(event);
      }}
      onPointerOver={tooltip.handlers.onPointerOver}
      onPointerOut={tooltip.handlers.onPointerOut}
      onFocus={onLaneFocus}
      onBlur={tooltip.handlers.onBlur}
      onKeyDown={onLaneKeyDown}
    >
      {/* L0 of the week view (grid lines and sleep shading) is drawn once by WeekGridLayer. */}
      {variant === LaneVariant.Day
        ? bands.map((band) => (
            <div
              key={band.startMin}
              className={styles.sleep}
              style={{
                top: `${percentOf(band.startMin, settings.dayStartMin)}%`,
                height: `${percentOf(band.endMin, settings.dayStartMin) - percentOf(band.startMin, settings.dayStartMin)}%`,
              }}
              title={t('timeline.sleep')}
              data-testid="sleep-band"
            />
          ))
        : null}

      {/* The 6px gap right of a week lane belongs to its create band: pointer events over the gap
          land here and bubble to the lane as an "empty" gesture (ui-redesign §3.1.3). */}
      {variant === LaneVariant.Week ? (
        <div className={styles.bandHit} data-testid="create-band" aria-hidden="true" />
      ) : null}

      {items.map((item) => {
        if (item.kind === LaneItemKind.Gap) {
          const slot = slotByStart.get(item.startMin);
          if (slot === undefined) return null;
          return (
            <button
              key={item.id}
              type="button"
              className={styles.gap}
              style={{
                top: `calc(${percentOf(slot.startMin, settings.dayStartMin)}% + 1px)`,
                height: `calc(${percentOf(slot.endMin, settings.dayStartMin) - percentOf(slot.startMin, settings.dayStartMin)}% - 2px)`,
              }}
              tabIndex={item.id === tabStopId ? 0 : -1}
              aria-label={t('timeline.recordSlot', { range: formatRange(slot.startMin, slot.endMin) })}
              data-testid="gap-slot"
              data-start={slot.startMin}
              data-lane-item={gapItemId(slot.startMin)}
              onClick={(event) => {
                // Pointer clicks are handled by the lane (they know the exact minute); this is the keyboard path.
                if (event.detail !== 0) return;
                const rect = event.currentTarget.getBoundingClientRect();
                createFromSlot(slot.startMin, {
                  left: rect.left,
                  top: rect.top,
                  right: rect.right,
                  bottom: rect.bottom,
                });
              }}
            >
              {showGapLabel(slot) ? <span className={styles.gapLabel}>{t('timeline.record')}</span> : null}
            </button>
          );
        }
        const group = groupByKey.get(item.id.slice('block:'.length));
        if (group === undefined) return null;
        const resolvingThis = resolvingIds.has(group.key);
        const dragged = draggedKey === group.key && preview !== null;
        const shifted = dragged && movePreview !== null;
        const layout = group.members.length === 1 ? conflicts.layout.get(group.key) : undefined;
        // Pixel width of the block's overlap columns: under 56px it shows no text (§3.1.4).
        const columnPx =
          layout === undefined || laneWidthPx === 0
            ? Infinity
            : ((laneWidthPx - createBandPx(laneWidthPx, variant)) * layout.span) / layout.columns;
        return (
          <TimeBlockCard
            key={item.id}
            group={group}
            project={projects.byId.get(group.projectId)}
            projectsById={projects.byId}
            dayStartMin={settings.dayStartMin}
            laneHeightPx={laneHeightPx}
            isNow={nowMin !== null && nowMin >= group.startMin && nowMin < group.endMin}
            selected={selectedKey === group.key}
            conflict={layout}
            overlapMarked={conflicts.marked.has(group.key)}
            stolen={stolen.get(group.key)}
            narrowColumn={columnPx < MIN_OVERLAP_TEXT_COLUMN_PX}
            note={variant === LaneVariant.Day ? noteFirstLineOf(group) : undefined}
            stackStepPx={variant === LaneVariant.Week ? STACK_OFFSET_WEEK_PX : STACK_OFFSET_DAY_PX}
            dragging={dragged}
            shiftPx={shifted ? moveShiftPx : undefined}
            shiftXPx={shifted ? crossShiftPx : undefined}
            shiftedSpan={shifted && movePreview !== null ? movePreview.span : undefined}
            armed={armed === ArmedKind.Move && armedGroupKey === group.key}
            quickColor={dragged ? undefined : quickColor}
            quickTip={
              quickProject === undefined
                ? undefined
                : group.projectId === quickProject.id
                  ? t('quickMark.alreadyTip', { project: quickProject.data.name })
                  : t('quickMark.addBesideTip', { project: quickProject.data.name })
            }
            learnHint={learnHint}
            interactive={ready}
            tabbable={item.id === tabStopId}
            resolving={resolvingThis}
            describedBy={tooltip.target?.element.dataset['groupKey'] === group.key ? tooltipId : undefined}
            dayLinkTo={variant === LaneVariant.Week ? dayPath(date) : undefined}
            weekLane={variant === LaneVariant.Week}
            onOpen={openGroup}
            onConflict={openGroupConflict}
          />
        );
      })}

      {showHover ? (
        <DragGhost
          span={hoverSpan}
          dayStartMin={settings.dayStartMin}
          variant={GhostVariant.Hover}
          showRange={ghostRangeFits}
          suffix={ghostRangeFits ? ghostSuffix : undefined}
          hint={ghostTextFits ? ghostHint : undefined}
          label={variant === LaneVariant.Week ? '+' : undefined}
        />
      ) : null}
      {originSpan !== null ? (
        <DragGhost span={originSpan} dayStartMin={settings.dayStartMin} variant={GhostVariant.Origin} />
      ) : null}
      {pendingSpan !== null ? (
        <DragGhost
          span={pendingSpan}
          dayStartMin={settings.dayStartMin}
          variant={GhostVariant.Pending}
          label={t('timeline.pending')}
        />
      ) : null}
      {tileDrag !== null ? (
        <DragGhost
          span={tileDrag.span}
          dayStartMin={settings.dayStartMin}
          variant={tileDrag.allowed ? GhostVariant.Drag : GhostVariant.Blocked}
        />
      ) : null}
      {preview?.kind === PreviewKind.Create ? (
        <DragGhost
          span={preview.span}
          dayStartMin={settings.dayStartMin}
          variant={GhostVariant.Drag}
          armed={armed === ArmedKind.Create}
        />
      ) : null}
      {/* Guide lines of the drag (§3.4.4); the week view draws them across all lanes instead. */}
      {preview !== null && variant === LaneVariant.Day
        ? [preview.span.startMin, preview.span.endMin].map((min) => (
            <div
              key={min}
              className={styles.guideLine}
              style={{ top: `${percentOf(min, settings.dayStartMin)}%` }}
              data-testid="guide-line"
            />
          ))
        : null}
      {/* The floating tip lives on document.body (L4): it never affects the lane's layout. */}
      {preview !== null ? <FloatTip span={preview.span} pointRef={dragPointRef} line2={tipLine2} /> : null}

      {nowMin !== null ? <NowLine nowMin={nowMin} dayStartMin={settings.dayStartMin} showLabel={variant === LaneVariant.Day} /> : null}

      {tooltip.target !== null ? (
        <TooltipBubble id={tooltipId} text={tooltip.target.text} anchor={tooltip.target.anchor} />
      ) : null}

      {data.blocksLoading ? (
        <div className={styles.skeleton} aria-hidden="true" data-testid="lane-skeleton">
          {SKELETON_BARS.map((bar) => (
            <span
              key={bar.top}
              className={styles.skeletonBar}
              style={{ top: `${bar.top}%`, height: `${bar.height}%` }}
            />
          ))}
        </div>
      ) : null}
      {data.blocksError !== null ? (
        <div className={styles.error} role="alert" data-testid="lane-error">
          <p>{t('timeline.loadError')}</p>
          <button type="button" className={styles.retry} onClick={data.retryBlocks}>
            {t('timeline.retry')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
